import React, { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import ConnectGoogleAdsButton from '@/components/ConnectGoogleAdsButton';
import { AdsAccountPickerDialog } from '@/components/ads/AdsAccountPickerDialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useChapterOpen } from '@/hooks/optimizationScore/chapterOpenPreference';
import type { useAdsInterest } from '@/hooks/useAdsInterest';
import type { AdsConnectFlow } from '@/hooks/useAdsConnectFlow';
import type { LinkedAdsAccount } from '@/hooks/useLinkedAdsAccount';
import { formatDecimal } from '@/lib/language';
import { maskCustomerId } from '@/lib/maskCustomerId';
import { formatMicros, shareWithoutAds, type AdsChapterState, type ParsedAdsFeedSummary } from '@/lib/adsFeedSummary';
import { AdsChapterBody } from './AdsChapterBody';

export interface AdsChapterRowProps {
  /** Stellung dieses Kapitels in der Gliederung, aus der Seite. Die Zeile
   *  rendert IMMER, also zaehlt sie auch immer mit; die frueher fest in der
   *  Copy stehende 3 war falsch, sobald der Feed keinen Preis-Benchmark hat. */
  chapterIndex: number;
  chapterTotal: number;
  feedId: string | null;
  feedName: string | null;
  feedLanguage: string | null;
  merchantCenterId: string | null;
  merchantCenterName: string | null;
  googleAccountId: string | null;
  state: AdsChapterState;
  linked: LinkedAdsAccount | null;
  parsed: ParsedAdsFeedSummary | null;
  flow: AdsConnectFlow;
  /** Ziel der Geldliste-Zeile "nie optimiert". */
  onOptimizeUnoptimized?: () => void;
  /** Ziel der Disapproved-Zeile. */
  onShowDisapproved?: () => void;
  /**
   * Ein Optimierungslauf der Seite laeuft schon (oder der Merchant-Zugriff ist
   * entzogen): die Geldlisten-Zeile "nie optimiert" ruft denselben Lauf und
   * darf ihn dann nicht ein zweites Mal starten.
   */
  fixBusy?: boolean;
  /**
   * Aufklapp-Bitte von aussen (Spec 2026-09-16, Abschnitt 6; Plan-Entscheidung
   * E4): der Anker `/#ads` und der Primaerknopf der Handlungskarte fuer
   * Schritt 5 bitten hierueber. Sie oeffnen das Kapitel NICHT ueber eine
   * zweite `useChapterOpen`-Instanz -- die haelt ihren Zustand je Instanz und
   * saehe die hier gehaltene nicht.
   *
   * Der Fokus wandert dabei auf den Connect-Knopf: die Handlungskarte
   * verspricht "Google-Ads-Konto verbinden", und der Ablauf dafuer lebt
   * ausschliesslich hier und in den Einstellungen. Ein zweiter Connect-Ablauf
   * in der Karte waere eine Kopie von `useAdsConnectFlow` samt Popup-Sperre
   * und Rueckweg-Behandlung.
   */
  openRequest?: { chapter: 'ads' | 'live'; tick: number } | null;
  /** Die EINE Teaser-Instanz der Seite (siehe Kommentar im Rumpf). */
  interest: ReturnType<typeof useAdsInterest>;
}

/**
 * Die Zeile "Kapitel 3 · Google Ads" in ihren sieben Zustaenden (Spec 2,
 * Zustandstabelle). Nur "ready", "stale" und "no_data" haben einen Aufklapper;
 * die vier Connect-Zustaende (inklusive "owner_managed" fuer Mitglieder, deren
 * Eigentuemer das Konto verwaltet) zeigen einen Satz und hoechstens eine Handlung.
 *
 * KEIN gefuellter Knopf. Der eine Primaer-CTA des Feed-Checks gehoert der
 * Handlungskarte oben (Verbot 10 des UX-Audits, gemessen ueber ALLE Knoepfe der
 * Seite in `src/pages/FeedHealthDashboard.test.tsx`). Kapitel 3 ist eine
 * eingeklappte Zeile darunter; ein zweiter gefuellter Knopf hier machte aus der
 * einen naechsten Handlung eine Auswahl zwischen zweien.
 */
export function AdsChapterRow(props: AdsChapterRowProps) {
  const { t, i18n } = useTranslation('optimization');
  const lang = i18n?.language;
  const [open, setOpen] = useChapterOpen(props.feedId, 'ads');
  const contentId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const letzterTick = useRef<number | null>(null);
  // EINE Instanz je Seite, von aussen hereingereicht (Spec 2026-09-16,
  // Abschnitt 3.2): seit dem Done-Zustand der Handlungskarte gibt es einen
  // ZWEITEN Ort mit demselben Teaser-Knopf. Zwei `useAdsInterest`-Instanzen
  // lesen zwar denselben `localStorage`-Marker, halten ihren `reported`-Zustand
  // aber je Instanz -- ein Klick in der Karte liesse den Knopf im Kapitel
  // weiterhin ungemeldet aussehen, und der Kunde meldete zweimal.
  const interest = props.interest;

  // Ein Aufklapper ohne Inhalt ist eine Sackgasse: gibt es keinen geparsten
  // Eintrag, gibt es auch dann keinen Knopf, wenn die Seite "ready" behauptet.
  const hasBody = !!props.parsed && !!props.linked
    && (props.state === 'ready' || props.state === 'stale' || props.state === 'no_data');
  const s = props.parsed?.summary;
  const line = !s
    ? null
    : s.cost_micros > 0 && s.roas !== null
      ? t('feedCheck.ads.summaryLine', {
        share: shareWithoutAds(s),
        cost: formatMicros(s.cost_micros, props.parsed?.row.currency ?? null, lang),
        roas: formatDecimal(s.roas, lang, 1),
      })
      : t('feedCheck.ads.summaryLineNoCost', { share: shareWithoutAds(s) });

  /**
   * Aufklappen, hinscrollen, Fokus setzen -- in dieser Reihenfolge und nur
   * einmal je Bitte (`letzterTick`). Der Fokus geht auf die eine Handlung des
   * Zustands: Verbinden (`not_connected`) oder Konto zuordnen (`unlinked`). In
   * allen anderen Zuständen gibt es keinen der beiden Knöpfe, dann bleibt der
   * Fokus, wo er ist.
   */
  useEffect(() => {
    if (!props.openRequest || props.openRequest.chapter !== 'ads') return;
    if (letzterTick.current === props.openRequest.tick) return;
    letzterTick.current = props.openRequest.tick;
    if (hasBody) setOpen(true);
    // Optionaler Aufruf: jsdom kennt `scrollIntoView` nicht, und ein Wurf hier
    // naehme das Aufklappen und den Fokus mit -- beides wichtiger als das
    // Scrollen selbst.
    rootRef.current?.scrollIntoView?.({ block: 'start' });
    // WP5 K22: im Zustand `unlinked` heisst die Handlung "Konto zuordnen"
    // (`ads-assign-cta`), nicht "verbinden". Reine Darstellung, kein
    // Ads-Datenpfad (Messfenster unberuehrt).
    const target = rootRef.current?.querySelector<HTMLElement>(
      '[data-testid="ads-connect-cta"], [data-testid="ads-assign-cta"]',
    );
    target?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `setOpen` ist pro Feed stabil, `hasBody` soll die Bitte nicht erneut ausloesen
  }, [props.openRequest]);

  return (
    <div ref={rootRef} data-testid={`chapter-ads-${props.state}`} className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-heading font-bold">{t('feedCheck.ads.label', { index: props.chapterIndex, total: props.chapterTotal })}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {props.state === 'not_connected' && t('feedCheck.ads.notConnected.line')}
            {props.state === 'unlinked' && t('feedCheck.ads.unlinked.line')}
            {props.state === 'owner_managed' && t('feedCheck.ads.ownerManaged.line')}
            {props.state === 'no_numbers' && props.linked && t('feedCheck.ads.noNumbers.line', { id: maskCustomerId(props.linked.adsCustomerId) })}
            {hasBody && line}
          </p>
        </div>
        {props.state === 'not_connected' && (
          <ConnectGoogleAdsButton returnTo="feed_check" variant="outline" className="print:hidden shrink-0" onConnected={props.flow.onConnected} disabled={props.flow.busy} testId="ads-connect-cta" />
        )}
        {/* "Konto zuordnen" NUR hier: im Zustand `not_connected` gibt es noch
            kein Konto, das man zuordnen koennte, und der Dialog stuende leer da. */}
        {props.state === 'unlinked' && (
          <Button data-testid="ads-assign-cta" variant="outline" size="sm" className="print:hidden shrink-0" disabled={props.flow.busy} onClick={props.flow.openPicker}>
            {t('feedCheck.ads.unlinked.cta')}
          </Button>
        )}
        {hasBody && (
          <button
            type="button"
            className="print:hidden shrink-0 text-sm font-medium text-primary hover:underline"
            aria-expanded={open}
            // NUR bei gerendertem Rumpf: `aria-controls` auf eine id, die es
            // im Dokument nicht gibt, ist fuer einen Screenreader ein Verweis
            // ins Leere. Der Rumpf haengt an `open`, also der Verweis auch.
            aria-controls={open ? contentId : undefined}
            onClick={() => setOpen(!open)}
          >
            {open ? t('feedCheck.ads.close') : t('feedCheck.ads.open')}
          </button>
        )}
      </div>

      {props.state === 'not_connected' && (
        <div className="flex flex-col gap-3">
          {/* Silhouetten statt Zahlen: welche Groessen das Kapitel zeigen wird,
              ist eine Zusage, die Zahlen darin waeren erfunden. */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 opacity-60" aria-hidden="true">
            {(['tileCost', 'tileRoas', 'tileNoSale'] as const).map((k) => (
              <div key={k} className="flex flex-col gap-1.5 rounded-md bg-muted/60 px-4 py-3.5">
                <span className="text-xs text-muted-foreground">{t(`feedCheck.ads.notConnected.${k}`)}</span>
                <span className="block h-6 w-24 rounded bg-border" />
                <span className="block h-2.5 w-36 rounded bg-muted" />
              </div>
            ))}
          </div>
          <p className="text-[11px] leading-relaxed text-muted-foreground">{t('feedCheck.ads.notConnected.trust')}</p>
        </div>
      )}

      {hasBody && open && props.parsed && props.linked && (
        <div id={contentId}>
          <AdsChapterBody
            parsed={props.parsed}
            linked={props.linked}
            state={props.state as 'ready' | 'stale' | 'no_data'}
            feedId={props.feedId}
            interest={interest}
            onOptimizeUnoptimized={props.onOptimizeUnoptimized}
            onShowDisapproved={props.onShowDisapproved}
            busy={props.fixBusy}
          />
        </div>
      )}

      <AdsAccountPickerDialog
        open={props.flow.pickerOpen}
        onOpenChange={(o) => (o ? props.flow.openPicker() : props.flow.closePicker())}
        candidates={props.flow.candidates}
        onConfirm={(id) => void props.flow.confirmLink(id)}
        isLinking={props.flow.isLinking}
      />
    </div>
  );
}

/**
 * Die Kapitelzeile, solange die Ads-Abfragen der Seite noch laufen.
 *
 * Ohne sie rendert die Zeile waehrend des Ladens `not_connected`: beide
 * Abfragen liefern `undefined`, und aus "noch nicht gewusst" wird "nicht
 * verbunden" samt Silhouetten und einem Verbinden-Knopf fuer einen Scope, den
 * der Kunde vielleicht laengst erteilt hat. Beim ersten Seitenaufbau faellt das
 * kaum auf; beim Wechsel auf einen Feed unter einem ZWEITEN Merchant Center mit
 * bereits gecachtem Befund rendert die Karte sofort, waehrend
 * `useLinkedAdsAccount` unter neuem Schluessel erst laedt.
 *
 * Die Ueberschrift steht schon da: die Gliederung ist bekannt, nur ihr Inhalt
 * nicht.
 */
export function AdsChapterRowSkeleton({ chapterIndex, chapterTotal }: { chapterIndex: number; chapterTotal: number }) {
  const { t } = useTranslation('optimization');
  return (
    <div data-testid="chapter-ads-loading" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-heading font-bold">{t('feedCheck.ads.label', { index: chapterIndex, total: chapterTotal })}</h2>
          <Skeleton className="mt-2 h-4 w-72 max-w-full" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" aria-hidden="true">
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-[86px] w-full" />)}
      </div>
    </div>
  );
}
